import { FC } from "react";
import { Box, LinearProgress, Typography } from "@mui/material";
import { gameState, useTeamSides } from "@/services/gameState";
import { useAppDataContext } from "@context/AppContext";
import { Confirmation, GameState } from "@/models/gameState";
import { TeamReference } from "@/models/pdfData";
import Jersey from "../custom-icons/Jersey";

const RenderProgress: FC<{
  team: GameState["teams"][TeamReference];
  teamSquad: GameState["teamSquads"][TeamReference];
  teamConfirmation: GameState["teamConfirmations"][TeamReference];
  gender: GameState["gender"];
}> = ({ team, teamSquad, teamConfirmation, gender }) => {
  const { players, officials } = team;
  const persons = [...players, ...officials];
  const teamSquadPersons = [...teamSquad.players, ...teamSquad.officials];
  const requiredLicenseChecks = teamSquadPersons.length;
  const licenseChecksCompleted = persons.filter(
    (p) => !!p.approvedOfficialLicense,
  ).length;

  const amountPlayers = teamSquad.players.length;
  const amountOfficials = teamSquad.officials.length;
  const amountLiberos = teamSquad.liberos.length;

  const requiredPlayers = Math.max(6 + amountLiberos, amountPlayers);
  const requiredOfficials = Math.max(1, amountOfficials);

  const readyForLicenseApproval =
    (amountPlayers - amountLiberos >= 6 &&
      !!teamSquad.captain &&
      teamConfirmation.valueOf() >= Confirmation.SQUAD) ||
    !!persons.find((p) => p.approvedOfficialLicense);

  const progressValue = readyForLicenseApproval
    ? licenseChecksCompleted / requiredLicenseChecks
    : (amountPlayers + amountOfficials) / (requiredPlayers + requiredOfficials);
  const progressText = readyForLicenseApproval
    ? `${licenseChecksCompleted}/${requiredLicenseChecks}`
    : `${amountPlayers} (+${amountOfficials}) / ${requiredPlayers} (+${requiredOfficials})`;

  const licenseApprovalComplete =
    teamConfirmation.valueOf() >= Confirmation.SQUAD &&
    licenseChecksCompleted >= requiredLicenseChecks;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexDirection: "column",
        width: "100%",
      }}
    >
      <Typography variant={"h5"}>
        {readyForLicenseApproval ? "Sichtkontrolle" : "Mannschaftsliste"}
      </Typography>
      <Box sx={{ width: "100%", mr: 1 }}>
        <LinearProgress
          variant="determinate"
          value={progressValue * 100}
          color={
            licenseApprovalComplete
              ? "success"
              : readyForLicenseApproval
                ? "primary"
                : "secondary"
          }
          sx={{ height: "3vw", borderRadius: "3vw" }}
        />
      </Box>
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {progressText}
      </Typography>
      <Box pt={"5vw"}>
        <Jersey
          gender={gender}
          jersey={team.jerseys[team.selectedJersey || "home"]}
        />
      </Box>
    </Box>
  );
};

const RefereeTabletLicenseConfirmedProgress: FC = () => {
  const teams = gameState("teams");
  const teamSquads = gameState("teamSquads");
  const teamConfirmations = gameState("teamConfirmations");
  const gender = gameState("gender");
  const { leftTeam, rightTeam } = useTeamSides(useAppDataContext());
  return (
    <Box
      display={"flex"}
      justifyContent={"center"}
      alignItems={"center"}
      gap={2}
      pt={3}
      flexDirection={"column"}
    >
      <Typography variant={"h4"}>Fortschritt Mannschaftslisten</Typography>
      <Box
        display={"flex"}
        gap={2}
        width={"100%"}
        justifyContent={"space-around"}
      >
        {teams && teamSquads && (
          <>
            <RenderProgress
              team={teams[leftTeam]}
              teamSquad={teamSquads[leftTeam]}
              teamConfirmation={teamConfirmations[leftTeam]}
              gender={gender}
            />
            <RenderProgress
              team={teams[rightTeam]}
              teamSquad={teamSquads[rightTeam]}
              teamConfirmation={teamConfirmations[rightTeam]}
              gender={gender}
            />
          </>
        )}
      </Box>
    </Box>
  );
};

export default RefereeTabletLicenseConfirmedProgress;
